/**
 * Las cuentas de la agenda del panel, puras y con su prueba: fechas como «AAAA-MM-DD» (las del
 * negocio, que da el núcleo ya en su zona), el rango de horas que se pinta y cómo se reparten las
 * reservas que se solapan en una misma columna (una clase o una zona del restaurante tienen varias a la vez).
 */

export type Estado = 'asistio' | 'cancelada' | 'confirmada' | 'no-vino' | 'pendiente' | 'por-pagar'

export const ESTADOS: Record<string, { texto: string; clave: 'construyendo' | 'fallida' | 'neutra' | 'publicado' | 'sin-publicar' | 'violeta' }> = {
  'por-pagar': { texto: 'Esperando el pago', clave: 'sin-publicar' },
  pendiente: { texto: 'Pendiente', clave: 'construyendo' },
  confirmada: { texto: 'Confirmada', clave: 'violeta' },
  asistio: { texto: 'Asistió', clave: 'publicado' },
  'no-vino': { texto: 'No vino', clave: 'fallida' },
  cancelada: { texto: 'Cancelada', clave: 'neutra' },
}

export const ORIGENES: Record<string, string> = { web: 'Web', panel: 'Panel', telefono: 'Teléfono', whatsapp: 'WhatsApp' }

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

const partes = (fecha: string) => fecha.split('-').map(Number) as [number, number, number]
const aFecha = (ms: number) => new Date(ms).toISOString().slice(0, 10)

export const esFecha = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`))

export const sumarDias = (fecha: string, n: number): string => {
  const [a, m, d] = partes(fecha)
  return aFecha(Date.UTC(a, m - 1, d + n))
}

/** 0 = domingo … 6 = sábado. */
export const diaDeLaSemana = (fecha: string): number => {
  const [a, m, d] = partes(fecha)
  return new Date(Date.UTC(a, m - 1, d)).getUTCDay()
}

/** El lunes de la semana de una fecha: la semana del negocio empieza en lunes. */
export const lunesDe = (fecha: string): string => sumarDias(fecha, -((diaDeLaSemana(fecha) + 6) % 7))

/** Hoy en una zona horaria (la del negocio, si se sabe). */
export const hoyEn = (zona?: string): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: zona, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
  } catch {
    return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
  }
}

/** «lunes 12 de octubre». */
export const textoDeFecha = (fecha: string, corto = false): string => {
  const [, m, d] = partes(fecha)
  const dia = DIAS[diaDeLaSemana(fecha)]!
  return corto ? `${dia.slice(0, 3)} ${d}` : `${dia} ${d} de ${MESES[m - 1]}`
}

/** «12 – 18 de octubre», o «28 de septiembre – 4 de octubre». */
export const textoDeSemana = (lunes: string): string => {
  const domingo = sumarDias(lunes, 6)
  const [, m1, d1] = partes(lunes)
  const [, m2, d2] = partes(domingo)
  return m1 === m2 ? `${d1} – ${d2} de ${MESES[m2 - 1]}` : `${d1} de ${MESES[m1 - 1]} – ${d2} de ${MESES[m2 - 1]}`
}

export const horaDeMin = (min: number): string => `${String(Math.floor(min / 60) % 24).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`

/**
 * De qué hora a qué hora se pinta la rejilla: lo abierto y lo reservado de esos días, redondeado a
 * horas enteras; si no hay nada, de 8 a 20.
 */
export const rangoDelDia = (franjas: Array<{ desdeMin: number; hastaMin: number }>, porOmision: [number, number] = [480, 1200]): [number, number] => {
  const visibles = franjas.filter((f) => f.hastaMin > f.desdeMin)
  if (visibles.length === 0) return porOmision
  const desde = Math.max(0, Math.floor(Math.min(...visibles.map((f) => f.desdeMin)) / 60) * 60)
  const hasta = Math.min(24 * 60, Math.ceil(Math.max(...visibles.map((f) => f.hastaMin)) / 60) * 60)
  return hasta - desde < 120 ? [desde, Math.min(24 * 60, desde + 120)] : [desde, hasta]
}

/**
 * Carriles para lo que se solapa en una columna: cada reserva va en el primer carril libre, y todas las
 * de un mismo grupo de solapes se reparten el ancho por igual.
 */
export const carriles = <T extends { id: number; inicioMin: number; finMin: number }>(lista: T[]): Map<number, { carril: number; de: number }> => {
  const orden = [...lista].sort((a, b) => a.inicioMin - b.inicioMin || b.finMin - a.finMin)
  const resultado = new Map<number, { carril: number; de: number }>()
  let grupo: T[] = []
  let finDelGrupo = -Infinity
  let finesPorCarril: number[] = []
  const cerrar = () => {
    for (const r of grupo) resultado.set(r.id, { carril: resultado.get(r.id)!.carril, de: finesPorCarril.length })
    grupo = []
    finesPorCarril = []
  }
  for (const r of orden) {
    if (r.inicioMin >= finDelGrupo) cerrar()
    let carril = finesPorCarril.findIndex((fin) => fin <= r.inicioMin)
    if (carril === -1) {
      carril = finesPorCarril.length
      finesPorCarril.push(r.finMin)
    } else finesPorCarril[carril] = r.finMin
    resultado.set(r.id, { carril, de: 0 })
    grupo.push(r)
    finDelGrupo = Math.max(finDelGrupo, r.finMin)
  }
  cerrar()
  return resultado
}
